import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Stack,
  Skeleton,
  Alert,
  Button,
  Divider,
} from "@mui/material";
import {
  BookmarkBorder,
  Delete,
  SmartToy,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import ReactHtmlParser from "react-html-parser";

const UserSavedDetails = () => {
  const [groupedSavedItems, setGroupedSavedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const user = JSON.parse(localStorage.getItem("UserDetails")) || {};
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.uid) {
      fetchAndGroupSavedContent();
    }
  }, [user?.uid]);

  const fetchAndGroupSavedContent = async () => {
    setLoading(true);
    setError("");
    try {
      console.log("Fetching saved content for user:", user.uid);
      
      const [questionsResponse, answersResponse, aiAnswersResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_FORUM_API}/api/save?user=${user.uid}`),
        axios.get(`${process.env.REACT_APP_FORUM_API}/api/save-answer?user=${user.uid}`),
        axios.get(`${process.env.REACT_APP_FORUM_API}/api/save-ai-answer?user=${user.uid}`),
      ]);

      const savedQuestions = questionsResponse.data.data || [];
      const savedAnswers = answersResponse.data.data || [];
      const savedAiAnswers = aiAnswersResponse.data.data || [];

      console.log("Raw data:", { savedQuestions, savedAnswers, savedAiAnswers });

      if (savedAiAnswers.length > 0) {
        console.log("First AI answer structure:", savedAiAnswers[0]);
        console.log("First AI answer questionId:", savedAiAnswers[0].questionId);
        console.log("Type of questionId:", typeof savedAiAnswers[0].questionId);
      }

      const questionGroups = new Map();

      savedQuestions.forEach(question => {
        const questionId = question.questionId?._id || question.questionId;
        console.log("Processing saved question, extracted questionId:", questionId);
        
        questionGroups.set(questionId, {
          questionId: questionId,
          questionData: question,
          isQuestionSaved: true,
          savedAnswers: [],
          savedAiAnswer: null,
          savedAt: question.createdAt
        });
      });

      savedAnswers.forEach(answer => {
        const questionId = answer.questionId?._id || answer.questionId;
        console.log("Processing saved answer, extracted questionId:", questionId);
        
        if (questionGroups.has(questionId)) {
          questionGroups.get(questionId).savedAnswers.push(answer);
        } else {
          const questionDataForAnswer = answer.questionId && typeof answer.questionId === 'object' 
            ? answer.questionId 
            : { questionId: questionId, title: answer.questionTitle };
            
          questionGroups.set(questionId, {
            questionId: questionId,
            questionData: questionDataForAnswer,
            isQuestionSaved: false,
            savedAnswers: [answer],
            savedAiAnswer: null,
            savedAt: answer.createdAt
          });
        }
      });

      savedAiAnswers.forEach((aiAnswer, index) => {
        console.log(`Processing AI answer ${index}:`, aiAnswer);
        console.log(`AI answer questionId type:`, typeof aiAnswer.questionId);
        console.log(`AI answer questionId value:`, aiAnswer.questionId);
        
        const questionId = aiAnswer.questionId?._id || aiAnswer.questionId;
        const questionDataForAi = aiAnswer.questionId && typeof aiAnswer.questionId === 'object' 
          ? aiAnswer.questionId 
          : { questionId: questionId, title: aiAnswer.questionTitle || "Question with AI Answer" };
          
        console.log(`Extracted questionId for AI answer:`, questionId);
        console.log(`Question data for AI:`, questionDataForAi);
        
        if (questionGroups.has(questionId)) {
          questionGroups.get(questionId).savedAiAnswer = aiAnswer;
        } else {
          questionGroups.set(questionId, {
            questionId: questionId,
            questionData: questionDataForAi,
            isQuestionSaved: false,
            savedAnswers: [],
            savedAiAnswer: aiAnswer,
            savedAt: aiAnswer.createdAt
          });
        }
      });

      const groupedArray = Array.from(questionGroups.values())
        .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));

      console.log("Grouped data:", groupedArray);
      setGroupedSavedItems(groupedArray);
    } catch (err) {
      console.error("Error fetching saved content:", err);
      setError("Failed to load saved content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const createdAt = moment(dateString);
    const now = moment();
    return now.diff(createdAt, "hours") < 24
      ? createdAt.fromNow()
      : createdAt.format("MMM DD, YYYY");
  };

  const truncateContent = (content, maxLength = 200) => {
    if (!content) return "";
    const textContent = content.replace(/<[^>]*>/g, "");
    return textContent.length > maxLength
      ? textContent.substring(0, maxLength) + "..."
      : textContent;
  };

  const handleQuestionClick = (item) => {
    console.log("=== Question Click Debug ===");
    console.log("Raw item:", item);
    console.log("Item questionId:", item.questionId);
    console.log("Type of questionId:", typeof item.questionId);
    
    let questionId;
    
    if (typeof item.questionId === 'string') {
      questionId = item.questionId;
    } else if (typeof item.questionId === 'object' && item.questionId !== null) {
      questionId = item.questionId._id || 
                 item.questionId.toString() ||
                 String(item.questionId);
    } else {
      questionId = String(item.questionId);
    }
    
    console.log("Extracted questionId:", questionId);
    console.log("Final questionId type:", typeof questionId);
    
    if (!questionId || questionId === '[object Object]' || questionId === 'undefined') {
      console.error('Invalid questionId extracted:', questionId);
      setError('Invalid question ID');
      return;
    }
    
    let url = `/question?q=${questionId}`;
    console.log("Navigation URL:", url);
    
    navigate(url);
  };

  const handleUnsaveQuestion = async (questionIdParam) => {
    try {
      const questionId = typeof questionIdParam === 'object' ? 
        (questionIdParam._id || questionIdParam.questionId || String(questionIdParam)) : 
        String(questionIdParam);
        
      console.log("Unsaving question with ID:", questionId);
      
      await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save/${questionId}`, {
        data: { user }
      });
      await fetchAndGroupSavedContent();
    } catch (err) {
      console.error("Error unsaving question:", err);
      setError("Failed to unsave question. Please try again.");
    }
  };

  const handleUnsaveAnswer = async (answerIdParam) => {
    try {
      const answerId = typeof answerIdParam === 'object' ? 
        (answerIdParam._id || answerIdParam.answerId || String(answerIdParam)) : 
        String(answerIdParam);
        
      console.log("Unsaving answer with ID:", answerId);
      
      await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save-answer/${answerId}`, {
        data: { user }
      });
      await fetchAndGroupSavedContent();
    } catch (err) {
      console.error("Error unsaving answer:", err);
      setError("Failed to unsave answer. Please try again.");
    }
  };

  const handleUnsaveAiAnswer = async (questionIdParam) => {
    try {
      console.log("=== AI Answer Delete Debug ===");
      console.log("Raw questionIdParam:", questionIdParam);
      console.log("Type:", typeof questionIdParam);
      console.log("JSON stringified:", JSON.stringify(questionIdParam));
      
      let questionId;
      
      if (typeof questionIdParam === 'string') {
        questionId = questionIdParam;
      } else if (typeof questionIdParam === 'object' && questionIdParam !== null) {
        questionId = questionIdParam._id || 
                   questionIdParam.questionId || 
                   questionIdParam.id ||
                   String(questionIdParam);
      } else {
        questionId = String(questionIdParam);
      }
      
      console.log("Extracted questionId:", questionId);
      console.log("Final questionId type:", typeof questionId);
      
      if (!questionId || questionId === '[object Object]' || questionId === 'undefined') {
        throw new Error('Invalid questionId extracted: ' + questionId);
      }
      
      await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save-ai-answer/${questionId}`, {
        data: { user }
      });
      await fetchAndGroupSavedContent();
    } catch (err) {
      console.error("Error unsaving AI answer:", err);
      setError("Failed to unsave AI answer. Please try again.");
    }
  };

  const QuestionGroupCard = ({ group }) => {
    const { questionData, isQuestionSaved, savedAnswers, savedAiAnswer } = group;
    
    const answerCount = questionData.answerDetails?.length || 
                       questionData.answerCount || 
                       (savedAnswers.length + (savedAiAnswer ? 1 : 0)) || 
                       0;
    
    return (
      <Box sx={{ 
        mb: 2,
        border: "1px solid #d9e3f0",
        borderRadius: "8px",
        bgcolor: "#ffffff",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(66, 133, 244, 0.08)",
          borderColor: "#b3d1ff"
        }
      }}>
        {/* Question Section */}
        <Box sx={{ 
          p: "16px 18px",
          background: "#fafbfd"
        }}>
          {/* Stats Row */}
          <Stack 
            direction="row" 
            spacing={2.5} 
            sx={{ 
              mb: 0.3,
              fontSize: "12px",
              color: "#5f6368"
            }}
          >
            <Stack direction="row" spacing={0.4} alignItems="center">
              <Typography sx={{ 
                fontWeight: 600, 
                color: "#202124",
                fontSize: "12px"
              }}>
                {questionData.voteCount || 0}
              </Typography>
              <Typography sx={{ fontSize: "12px" }}>
                votes
              </Typography>
            </Stack>
            
            <Stack direction="row" spacing={0.4} alignItems="center">
              <Typography sx={{ 
                fontWeight: 600, 
                color: answerCount > 0 ? "#1e8e3e" : "#5f6368",
                fontSize: "12px"
              }}>
                {answerCount}
              </Typography>
              <Typography sx={{ fontSize: "12px" }}>
                {answerCount === 1 ? "answer" : "answers"}
              </Typography>
            </Stack>
            
            {questionData.viewCount && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <Typography sx={{ 
                  fontWeight: 600, 
                  color: "#202124",
                  fontSize: "12px"
                }}>
                  {questionData.viewCount}
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  views
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Question Title and Content */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, pr: 1.5 }}>
              {/* Question Title */}
              <Typography
                variant="h6"
                onClick={() => handleQuestionClick({ questionId: questionData.questionId || questionData._id })}
                sx={{
                  color: "#1967d2",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: 500,
                  lineHeight: 1.3,
                  mb: 0.3,
                  transition: "color 0.2s ease",
                  "&:hover": { 
                    color: "#1557b0"
                  },
                }}
              >
                {questionData.title}
              </Typography>

              {/* Question Body */}
              {isQuestionSaved && questionData.body && (
                <Typography
                  variant="body2"
                  sx={{ 
                    color: "#3c4043", 
                    lineHeight: 1.5, 
                    mb: .7,
                    fontSize: "13px",
                    fontWeight: 400
                  }}
                >
                  {ReactHtmlParser(truncateContent(questionData.body, 150))}
                </Typography>
              )}

              {/* Tags and Meta Info Row */}
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                sx={{ mt: 0.75 }}
              >
                {/* Tags */}
                {isQuestionSaved && questionData.tagDetails && questionData.tagDetails.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {questionData.tagDetails.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag.name}
                        size="small"
                        sx={{
                          bgcolor: "#e8f0fe",
                          color: "#1967d2",
                          fontSize: "11px",
                          height: "22px",
                          borderRadius: "4px",
                          fontWeight: 500,
                          border: "1px solid #d2e3fc",
                          "& .MuiChip-label": {
                            px: "8px"
                          },
                          "&:hover": { 
                            bgcolor: "#d2e3fc"
                          },
                          transition: "all 0.2s ease"
                        }}
                      />
                    ))}
                  </Stack>
                )}

                {/* Save info */}
                <Typography variant="caption" sx={{ 
                  color: "#5f6368",
                  fontSize: "11px",
                  ml: "auto"
                }}>
                  Saved {formatDate(group.savedAt)}
                </Typography>
              </Stack>
            </Box>

            {/* Unsave Question Button */}
            {isQuestionSaved && (
              <IconButton
                onClick={() => handleUnsaveQuestion(group.questionId)}
                sx={{ 
                  color: "#5f6368",
                  "&:hover": { 
                    color: "#d93025",
                    bgcolor: "#fce8e6"
                  },
                  transition: "all 0.2s ease",
                  mt: -0.5
                }}
                size="small"
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>

        {/* Answers Section */}
        {(savedAnswers.length > 0 || savedAiAnswer) && (
          <Box sx={{ ml: { xs: 2, md: 3 }, mt: 1, mb: 1 }}>
            {/* Saved AI Answer */}
            {savedAiAnswer && (
              <Box sx={{ 
                borderLeft: "3px solid #4285f4",
                bgcolor: "#f8fbff",
                borderRadius: "0 4px 4px 0",
                p: "8px 10px",
                mb: 1.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#f1f7ff"
                }
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex: 1, pr: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <SmartToy sx={{ color: "#4285f4", fontSize: "16px" }} />
                      <Typography variant="caption" sx={{ 
                        color: "#4285f4", 
                        fontWeight: 600,
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        AI Answer
                      </Typography>
                    </Stack>
                    
                    <Typography variant="body2" sx={{ 
                      color: "#3c4043", 
                      lineHeight: 1.5, 
                      mb: 0.75,
                      fontSize: "12px",
                      fontWeight: 400
                    }}>
                      {truncateContent(savedAiAnswer.aiAnswer, 160)}
                    </Typography>
                    
                    <Typography variant="caption" sx={{ 
                      color: "#5f6368",
                      fontSize: "11px",
                      fontWeight: 400
                    }}>
                      {savedAiAnswer.voteCount || 0} votes • Saved {formatDate(savedAiAnswer.createdAt)}
                    </Typography>
                  </Box>
                  
                  <IconButton
                    onClick={() => handleUnsaveAiAnswer(group.questionId)}
                    sx={{ 
                      color: "#5f6368",
                      "&:hover": { 
                        color: "#d93025",
                        bgcolor: "#fce8e6"
                      },
                      transition: "all 0.2s ease"
                    }}
                    size="small"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            )}

            {/* Saved Answers */}
            {savedAnswers.map((answer, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Box sx={{ 
                  borderLeft: "3px solid #34a853",
                  bgcolor: "#f8fdf9",
                  borderRadius: "0 4px 4px 0",
                  p: "8px 10px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#f1f9f2"
                  }
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1, pr: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <CheckCircle sx={{ color: "#34a853", fontSize: "16px" }} />
                        <Typography variant="caption" sx={{ 
                          color: "#34a853", 
                          fontWeight: 600,
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px"
                        }}>
                          Answer
                        </Typography>
                      </Stack>
                      
                      <Typography variant="body2" sx={{ 
                        color: "#3c4043", 
                        lineHeight: 1.5, 
                        mb: 0.75,
                        fontSize: "12px",
                        fontWeight: 400
                      }}>
                        {ReactHtmlParser(truncateContent(answer.answer, 160))}
                      </Typography>
                      
                      <Typography variant="caption" sx={{ 
                        color: "#5f6368",
                        fontSize: "11px",
                        fontWeight: 400
                      }}>
                        {answer.voteCount || 0} votes • Saved {formatDate(answer.createdAt)}
                      </Typography>
                    </Box>
                    
                    <IconButton
                      onClick={() => handleUnsaveAnswer(answer.answerId)}
                      sx={{ 
                        color: "#5f6368",
                        "&:hover": { 
                          color: "#d93025",
                          bgcolor: "#fce8e6"
                        },
                        transition: "all 0.2s ease"
                      }}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const LoadingSkeleton = () => (
    <Box>
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ 
          mb: 2,
          border: "1px solid #d9e3f0",
          borderRadius: "8px",
          bgcolor: "#ffffff"
        }}>
          <Box sx={{ p: "16px" }}>
            <Stack direction="row" spacing={2.5} sx={{ mb: 0.75 }}>
              <Skeleton variant="text" width={60} height={16} />
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={70} height={16} />
            </Stack>
            
            <Skeleton variant="text" width="90%" height={22} sx={{ mb: 0.75 }} />
            <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
            
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={0.5}>
                <Skeleton variant="rectangular" width={50} height={20} />
                <Skeleton variant="rectangular" width={70} height={20} />
              </Stack>
              <Skeleton variant="text" width={80} height={14} />
            </Stack>
          </Box>
        </Box>
      ))}
    </Box>
  );

  if (!user?.uid) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Please log in to view your saved content
        </Typography>
        <Button
          onClick={() => navigate("/login")}
          variant="contained"
          sx={{ bgcolor: "#1967d2", "&:hover": { bgcolor: "#1557b0" } }}
        >
          Log In
        </Button>
      </Box>
    );
  }

  const totalItems = groupedSavedItems.reduce((sum, group) => {
    return sum + (group.isQuestionSaved ? 1 : 0) + group.savedAnswers.length + (group.savedAiAnswer ? 1 : 0);
  }, 0);

  return (
    <Box sx={{ 
      maxWidth: "1100px", 
      mx: "auto", 
      p: "24px",
      bgcolor: "#f8f9fa",
      minHeight: "100vh"
    }}>
      {/* Header */}
      <Box sx={{
        mb: 3,
        p: "24px",
        borderRadius: "8px",
        background: "linear-gradient(135deg, #4285f4 0%, #1967d2 100%)",
        boxShadow: "0 2px 8px rgba(66, 133, 244, 0.2)",
        color: "white"
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 600, 
              fontSize: "26px",
              mb: 0.5
            }}>
              All saves
            </Typography>
            <Typography variant="body2" sx={{ 
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "14px",
              fontWeight: 400
            }}>
              {totalItems} saved item{totalItems !== 1 ? 's' : ''}
            </Typography>
          </Box>
          
          <Button
            onClick={() => navigate("/add-question")}
            variant="contained"
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              textTransform: "none",
              fontSize: "14px",
              px: "18px",
              py: "8px",
              borderRadius: "6px",
              fontWeight: 500,
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              "&:hover": { 
                bgcolor: "rgba(255, 255, 255, 0.3)"
              },
              transition: "all 0.2s ease"
            }}
          >
            Ask Question
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <Box>
          {groupedSavedItems.length === 0 ? (
            <Box sx={{ 
              textAlign: "center", 
              py: 6,
              border: "1px solid #d9e3f0",
              borderRadius: "8px",
              bgcolor: "#fafbfd"
            }}>
              <BookmarkBorder sx={{ fontSize: 48, color: "#9aa0a6", mb: 2 }} />
              <Typography variant="h6" sx={{ 
                color: "#5f6368", 
                mb: 0.5,
                fontSize: "18px",
                fontWeight: 500
              }}>
                No saved items yet
              </Typography>
              <Typography variant="body2" sx={{ 
                color: "#5f6368",
                fontSize: "13px"
              }}>
                Save questions, answers, and AI responses to find them easily later
              </Typography>
            </Box>
          ) : (
            groupedSavedItems.map((group, index) => (
              <QuestionGroupCard key={`${group.questionId}-${index}`} group={group} />
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default UserSavedDetails;


// commented on 30 september 2025
// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Chip,
//   IconButton,
//   Card,
//   CardContent,
//   Stack,
//   Skeleton,
//   Alert,
//   Button,
//   Divider,
// } from "@mui/material";
// import {
//   BookmarkBorder,
//   Delete,
//   SmartToy,
//   CheckCircle,
// } from "@mui/icons-material";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import moment from "moment";
// import ReactHtmlParser from "react-html-parser";

// const UserSavedDetails = () => {
//   const [groupedSavedItems, setGroupedSavedItems] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   const user = JSON.parse(localStorage.getItem("UserDetails")) || {};
//   const navigate = useNavigate();

//   useEffect(() => {
//     if (user?.uid) {
//       fetchAndGroupSavedContent();
//     }
//   }, [user?.uid]);

//   const fetchAndGroupSavedContent = async () => {
//     setLoading(true);
//     setError("");
//     try {
//       console.log("Fetching saved content for user:", user.uid);
      
//       const [questionsResponse, answersResponse, aiAnswersResponse] = await Promise.all([
//         axios.get(`${process.env.REACT_APP_FORUM_API}/api/save?user=${user.uid}`),
//         axios.get(`${process.env.REACT_APP_FORUM_API}/api/save-answer?user=${user.uid}`),
//         axios.get(`${process.env.REACT_APP_FORUM_API}/api/save-ai-answer?user=${user.uid}`),
//       ]);

//       const savedQuestions = questionsResponse.data.data || [];
//       const savedAnswers = answersResponse.data.data || [];
//       const savedAiAnswers = aiAnswersResponse.data.data || [];

//       console.log("Raw data:", { savedQuestions, savedAnswers, savedAiAnswers });

//       if (savedAiAnswers.length > 0) {
//         console.log("First AI answer structure:", savedAiAnswers[0]);
//         console.log("First AI answer questionId:", savedAiAnswers[0].questionId);
//         console.log("Type of questionId:", typeof savedAiAnswers[0].questionId);
//       }

//       const questionGroups = new Map();

//       savedQuestions.forEach(question => {
//         const questionId = question.questionId?._id || question.questionId;
//         console.log("Processing saved question, extracted questionId:", questionId);
        
//         questionGroups.set(questionId, {
//           questionId: questionId,
//           questionData: question,
//           isQuestionSaved: true,
//           savedAnswers: [],
//           savedAiAnswer: null,
//           savedAt: question.createdAt
//         });
//       });

//       savedAnswers.forEach(answer => {
//         const questionId = answer.questionId?._id || answer.questionId;
//         console.log("Processing saved answer, extracted questionId:", questionId);
        
//         if (questionGroups.has(questionId)) {
//           questionGroups.get(questionId).savedAnswers.push(answer);
//         } else {
//           const questionDataForAnswer = answer.questionId && typeof answer.questionId === 'object' 
//             ? answer.questionId 
//             : { questionId: questionId, title: answer.questionTitle };
            
//           questionGroups.set(questionId, {
//             questionId: questionId,
//             questionData: questionDataForAnswer,
//             isQuestionSaved: false,
//             savedAnswers: [answer],
//             savedAiAnswer: null,
//             savedAt: answer.createdAt
//           });
//         }
//       });

//       savedAiAnswers.forEach((aiAnswer, index) => {
//         console.log(`Processing AI answer ${index}:`, aiAnswer);
//         console.log(`AI answer questionId type:`, typeof aiAnswer.questionId);
//         console.log(`AI answer questionId value:`, aiAnswer.questionId);
        
//         const questionId = aiAnswer.questionId?._id || aiAnswer.questionId;
//         const questionDataForAi = aiAnswer.questionId && typeof aiAnswer.questionId === 'object' 
//           ? aiAnswer.questionId 
//           : { questionId: questionId, title: aiAnswer.questionTitle || "Question with AI Answer" };
          
//         console.log(`Extracted questionId for AI answer:`, questionId);
//         console.log(`Question data for AI:`, questionDataForAi);
        
//         if (questionGroups.has(questionId)) {
//           questionGroups.get(questionId).savedAiAnswer = aiAnswer;
//         } else {
//           questionGroups.set(questionId, {
//             questionId: questionId,
//             questionData: questionDataForAi,
//             isQuestionSaved: false,
//             savedAnswers: [],
//             savedAiAnswer: aiAnswer,
//             savedAt: aiAnswer.createdAt
//           });
//         }
//       });

//       const groupedArray = Array.from(questionGroups.values())
//         .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));

//       console.log("Grouped data:", groupedArray);
//       setGroupedSavedItems(groupedArray);
//     } catch (err) {
//       console.error("Error fetching saved content:", err);
//       setError("Failed to load saved content. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatDate = (dateString) => {
//     const createdAt = moment(dateString);
//     const now = moment();
//     return now.diff(createdAt, "hours") < 24
//       ? createdAt.fromNow()
//       : createdAt.format("MMM DD, YYYY");
//   };

//   const truncateContent = (content, maxLength = 200) => {
//     if (!content) return "";
//     const textContent = content.replace(/<[^>]*>/g, "");
//     return textContent.length > maxLength
//       ? textContent.substring(0, maxLength) + "..."
//       : textContent;
//   };

//   const handleQuestionClick = (item) => {
//     console.log("=== Question Click Debug ===");
//     console.log("Raw item:", item);
//     console.log("Item questionId:", item.questionId);
//     console.log("Type of questionId:", typeof item.questionId);
    
//     let questionId;
    
//     if (typeof item.questionId === 'string') {
//       questionId = item.questionId;
//     } else if (typeof item.questionId === 'object' && item.questionId !== null) {
//       questionId = item.questionId._id || 
//                  item.questionId.toString() ||
//                  String(item.questionId);
//     } else {
//       questionId = String(item.questionId);
//     }
    
//     console.log("Extracted questionId:", questionId);
//     console.log("Final questionId type:", typeof questionId);
    
//     if (!questionId || questionId === '[object Object]' || questionId === 'undefined') {
//       console.error('Invalid questionId extracted:', questionId);
//       setError('Invalid question ID');
//       return;
//     }
    
//     let url = `/question?q=${questionId}`;
//     console.log("Navigation URL:", url);
    
//     navigate(url);
//   };

//   const handleUnsaveQuestion = async (questionIdParam) => {
//     try {
//       const questionId = typeof questionIdParam === 'object' ? 
//         (questionIdParam._id || questionIdParam.questionId || String(questionIdParam)) : 
//         String(questionIdParam);
        
//       console.log("Unsaving question with ID:", questionId);
      
//       await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save/${questionId}`, {
//         data: { user }
//       });
//       await fetchAndGroupSavedContent();
//     } catch (err) {
//       console.error("Error unsaving question:", err);
//       setError("Failed to unsave question. Please try again.");
//     }
//   };

//   const handleUnsaveAnswer = async (answerIdParam) => {
//     try {
//       const answerId = typeof answerIdParam === 'object' ? 
//         (answerIdParam._id || answerIdParam.answerId || String(answerIdParam)) : 
//         String(answerIdParam);
        
//       console.log("Unsaving answer with ID:", answerId);
      
//       await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save-answer/${answerId}`, {
//         data: { user }
//       });
//       await fetchAndGroupSavedContent();
//     } catch (err) {
//       console.error("Error unsaving answer:", err);
//       setError("Failed to unsave answer. Please try again.");
//     }
//   };

//   const handleUnsaveAiAnswer = async (questionIdParam) => {
//     try {
//       console.log("=== AI Answer Delete Debug ===");
//       console.log("Raw questionIdParam:", questionIdParam);
//       console.log("Type:", typeof questionIdParam);
//       console.log("JSON stringified:", JSON.stringify(questionIdParam));
      
//       let questionId;
      
//       if (typeof questionIdParam === 'string') {
//         questionId = questionIdParam;
//       } else if (typeof questionIdParam === 'object' && questionIdParam !== null) {
//         questionId = questionIdParam._id || 
//                    questionIdParam.questionId || 
//                    questionIdParam.id ||
//                    String(questionIdParam);
//       } else {
//         questionId = String(questionIdParam);
//       }
      
//       console.log("Extracted questionId:", questionId);
//       console.log("Final questionId type:", typeof questionId);
      
//       if (!questionId || questionId === '[object Object]' || questionId === 'undefined') {
//         throw new Error('Invalid questionId extracted: ' + questionId);
//       }
      
//       await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save-ai-answer/${questionId}`, {
//         data: { user }
//       });
//       await fetchAndGroupSavedContent();
//     } catch (err) {
//       console.error("Error unsaving AI answer:", err);
//       setError("Failed to unsave AI answer. Please try again.");
//     }
//   };

//   const QuestionGroupCard = ({ group }) => {
//     const { questionData, isQuestionSaved, savedAnswers, savedAiAnswer } = group;
    
//     const answerCount = questionData.answerDetails?.length || 
//                        questionData.answerCount || 
//                        (savedAnswers.length + (savedAiAnswer ? 1 : 0)) || 
//                        0;
    
//     return (
//       <Box sx={{ 
//         mb: 3,
//         border: "1px solid #e1e5e9",
//         borderRadius: "12px",
//         bgcolor: "#ffffff",
//         overflow: "hidden",
//         boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
//         transition: "all 0.3s ease",
//         "&:hover": {
//           boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
//           transform: "translateY(-2px)",
//           borderColor: "#c8d6e5"
//         }
//       }}>
//         {/* Question Section */}
//         <Box sx={{ 
//           p: "24px 24px 20px 24px",
//           background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
//         }}>
//           {/* Stats Row */}
//           <Stack 
//             direction="row" 
//             spacing={3} 
//             sx={{ 
//               mb: 1,
//               fontSize: "13px",
//               color: "#6a737c"
//             }}
//           >
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography sx={{ 
//                 fontWeight: 700, 
//                 color: "#0c0d0e",
//                 fontSize: "13px"
//               }}>
//                 {questionData.voteCount || 0}
//               </Typography>
//               <Typography sx={{ fontSize: "13px" }}>
//                 votes
//               </Typography>
//             </Stack>
            
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography sx={{ 
//                 fontWeight: 700, 
//                 color: answerCount > 0 ? "#2f6f44" : "#6a737c",
//                 fontSize: "13px"
//               }}>
//                 {answerCount}
//               </Typography>
//               <Typography sx={{ fontSize: "13px" }}>
//                 {answerCount === 1 ? "answer" : "answers"}
//               </Typography>
//             </Stack>
            
//             {questionData.viewCount && (
//               <Stack direction="row" spacing={0.5} alignItems="center">
//                 <Typography sx={{ 
//                   fontWeight: 700, 
//                   color: "#0c0d0e",
//                   fontSize: "13px"
//                 }}>
//                   {questionData.viewCount}
//                 </Typography>
//                 <Typography sx={{ fontSize: "13px" }}>
//                   views
//                 </Typography>
//               </Stack>
//             )}
//           </Stack>

//           {/* Question Title and Content */}
//           <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//             <Box sx={{ flex: 1, pr: 2 }}>
//               {/* Question Title - Larger */}
//               <Typography
//                 variant="h6"
//                 onClick={() => handleQuestionClick({ questionId: questionData.questionId || questionData._id })}
//                 sx={{
//                   color: "#2c5aa0",
//                   cursor: "pointer",
//                   fontSize: "19px",
//                   fontWeight: 600,
//                   lineHeight: 1.3,
//                   mb: 1.5,
//                   background: "linear-gradient(135deg, #2c5aa0, #0077cc)",
//                   backgroundClip: "text",
//                   WebkitBackgroundClip: "text",
//                   WebkitTextFillColor: "transparent",
//                   transition: "all 0.2s ease",
//                   "&:hover": { 
//                     transform: "translateX(4px)",
//                     filter: "brightness(1.1)"
//                   },
//                 }}
//               >
//                 {questionData.title}
//               </Typography>

//               {/* Question Body */}
//               {isQuestionSaved && questionData.body && (
//                 <Typography
//                   variant="body2"
//                   sx={{ 
//                     color: "#2d3748", 
//                     lineHeight: 1.6, 
//                     mb: 2.5,
//                     fontSize: "15px",
//                     fontWeight: 400
//                   }}
//                 >
//                   {ReactHtmlParser(truncateContent(questionData.body, 150))}
//                 </Typography>
//               )}

//               {/* Tags and Meta Info Row */}
//               <Stack 
//                 direction="row" 
//                 justifyContent="space-between" 
//                 alignItems="center"
//                 sx={{ mt: 1 }}
//               >
//                 {/* Tags */}
//                 {isQuestionSaved && questionData.tagDetails && questionData.tagDetails.length > 0 && (
//                   <Stack direction="row" spacing={0.5} flexWrap="wrap">
//                     {questionData.tagDetails.map((tag, index) => (
//                       <Chip
//                         key={index}
//                         label={tag.name}
//                         size="small"
//                         sx={{
//                           background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//                           color: "#ffffff",
//                           fontSize: "12px",
//                           height: "24px",
//                           borderRadius: "12px",
//                           fontWeight: 500,
//                           boxShadow: "0 2px 4px rgba(102, 126, 234, 0.3)",
//                           "& .MuiChip-label": {
//                             px: "8px"
//                           },
//                           "&:hover": { 
//                             transform: "translateY(-1px)",
//                             boxShadow: "0 4px 8px rgba(102, 126, 234, 0.4)"
//                           },
//                           transition: "all 0.2s ease"
//                         }}
//                       />
//                     ))}
//                   </Stack>
//                 )}

//                 {/* Save info */}
//                 <Typography variant="caption" sx={{ 
//                   color: "#6a737c",
//                   fontSize: "12px",
//                   ml: "auto"
//                 }}>
//                   Saved in For later {formatDate(group.savedAt)}
//                 </Typography>
//               </Stack>
//             </Box>

//             {/* Unsave Question Button */}
//             {isQuestionSaved && (
//               <IconButton
//                 onClick={() => handleUnsaveQuestion(group.questionId)}
//                 sx={{ 
//                   color: "#a0aec0",
//                   bgcolor: "rgba(255, 255, 255, 0.8)",
//                   backdropFilter: "blur(10px)",
//                   boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
//                   "&:hover": { 
//                     color: "#e53e3e",
//                     bgcolor: "rgba(229, 62, 62, 0.1)",
//                     transform: "scale(1.1)"
//                   },
//                   transition: "all 0.2s ease",
//                   mt: -0.5
//                 }}
//                 size="small"
//               >
//                 <Delete fontSize="small" />
//               </IconButton>
//             )}
//           </Stack>
//         </Box>

//         {/* Answers Section - Indented */}
//         {(savedAnswers.length > 0 || savedAiAnswer) && (
//           <Box sx={{ ml: { xs: 2, md: 4 }, mt: 2 }}>
//             {/* Saved AI Answer */}
//             {savedAiAnswer && (
//               <Box sx={{ 
//                 borderLeft: "3px solid #0077cc",
//                 bgcolor: "linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)",
//                 borderRadius: "0 8px 8px 0",
//                 p: "16px 20px",
//                 mb: 2,
//                 position: "relative",
//                 boxShadow: "0 2px 8px rgba(0, 119, 204, 0.08)",
//                 transition: "all 0.2s ease",
//                 "&:hover": {
//                   boxShadow: "0 4px 16px rgba(0, 119, 204, 0.12)",
//                   transform: "translateY(-1px)"
//                 },
//                 "&::before": {
//                   content: '""',
//                   position: "absolute",
//                   top: 0,
//                   left: 0,
//                   right: 0,
//                   height: "1px",
//                   background: "linear-gradient(90deg, #0077cc, transparent)"
//                 }
//               }}>
//                 <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                   <Box sx={{ flex: 1, pr: 2 }}>
//                     <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
//                       <Box sx={{
//                         p: 0.5,
//                         borderRadius: "6px",
//                         bgcolor: "rgba(0, 119, 204, 0.1)",
//                         display: "flex",
//                         alignItems: "center"
//                       }}>
//                         <SmartToy sx={{ color: "#0077cc", fontSize: "18px" }} />
//                       </Box>
//                       <Typography variant="caption" sx={{ 
//                         color: "#0077cc", 
//                         fontWeight: 700,
//                         fontSize: "12px",
//                         textTransform: "uppercase",
//                         letterSpacing: "1px",
//                         background: "linear-gradient(135deg, #0077cc, #0099ff)",
//                         backgroundClip: "text",
//                         WebkitBackgroundClip: "text",
//                         WebkitTextFillColor: "transparent"
//                       }}>
//                         AI Answer
//                       </Typography>
//                     </Stack>
                    
//                     <Typography variant="body2" sx={{ 
//                       color: "#1a1a1a", 
//                       lineHeight: 1.6, 
//                       mb: 1.5,
//                       fontSize: "14px",
//                       fontWeight: 400
//                     }}>
//                       {truncateContent(savedAiAnswer.aiAnswer, 180)}
//                     </Typography>
                    
//                     <Box sx={{
//                       display: "flex",
//                       alignItems: "center",
//                       gap: 1,
//                       p: 1,
//                       bgcolor: "rgba(255, 255, 255, 0.6)",
//                       borderRadius: "6px",
//                       backdropFilter: "blur(10px)"
//                     }}>
//                       <Typography variant="caption" sx={{ 
//                         color: "#666",
//                         fontSize: "11px",
//                         fontWeight: 500
//                       }}>
//                         {savedAiAnswer.voteCount || 0} votes • Saved {formatDate(savedAiAnswer.createdAt)}
//                       </Typography>
//                     </Box>
//                   </Box>
                  
//                   <IconButton
//                     onClick={() => handleUnsaveAiAnswer(group.questionId)}
//                     sx={{ 
//                       color: "#999",
//                       bgcolor: "rgba(255, 255, 255, 0.8)",
//                       backdropFilter: "blur(10px)",
//                       "&:hover": { 
//                         color: "#ff4757",
//                         bgcolor: "rgba(255, 71, 87, 0.1)",
//                         transform: "scale(1.05)"
//                       },
//                       transition: "all 0.2s ease",
//                       boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
//                     }}
//                     size="small"
//                   >
//                     <Delete fontSize="small" />
//                   </IconButton>
//                 </Stack>
//               </Box>
//             )}

//             {/* Saved Answers */}
//             {savedAnswers.map((answer, index) => (
//               <Box key={index} sx={{ mb: 2 }}>
//                 <Box sx={{ 
//                   borderLeft: "3px solid #2f6f44",
//                   bgcolor: "linear-gradient(135deg, #f0fff4 0%, #e6ffed 100%)",
//                   borderRadius: "0 8px 8px 0",
//                   p: "16px 20px",
//                   position: "relative",
//                   boxShadow: "0 2px 8px rgba(47, 111, 68, 0.08)",
//                   transition: "all 0.2s ease",
//                   "&:hover": {
//                     boxShadow: "0 4px 16px rgba(47, 111, 68, 0.12)",
//                     transform: "translateY(-1px)"
//                   },
//                   "&::before": {
//                     content: '""',
//                     position: "absolute",
//                     top: 0,
//                     left: 0,
//                     right: 0,
//                     height: "1px",
//                     background: "linear-gradient(90deg, #2f6f44, transparent)"
//                   }
//                 }}>
//                   <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                     <Box sx={{ flex: 1, pr: 2 }}>
//                       <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
//                         <Box sx={{
//                           p: 0.5,
//                           borderRadius: "6px",
//                           bgcolor: "rgba(47, 111, 68, 0.1)",
//                           display: "flex",
//                           alignItems: "center"
//                         }}>
//                           <CheckCircle sx={{ color: "#2f6f44", fontSize: "18px" }} />
//                         </Box>
//                         <Typography variant="caption" sx={{ 
//                           color: "#2f6f44", 
//                           fontWeight: 700,
//                           fontSize: "12px",
//                           textTransform: "uppercase",
//                           letterSpacing: "1px",
//                           background: "linear-gradient(135deg, #2f6f44, #4caf50)",
//                           backgroundClip: "text",
//                           WebkitBackgroundClip: "text",
//                           WebkitTextFillColor: "transparent"
//                         }}>
//                           Answer
//                         </Typography>
//                       </Stack>
                      
//                       <Typography variant="body2" sx={{ 
//                         color: "#1a1a1a", 
//                         lineHeight: 1.6, 
//                         mb: 1.5,
//                         fontSize: "14px",
//                         fontWeight: 400
//                       }}>
//                         {ReactHtmlParser(truncateContent(answer.answer, 180))}
//                       </Typography>
                      
//                       <Box sx={{
//                         display: "flex",
//                         alignItems: "center",
//                         gap: 1,
//                         p: 1,
//                         bgcolor: "rgba(255, 255, 255, 0.6)",
//                         borderRadius: "6px",
//                         backdropFilter: "blur(10px)"
//                       }}>
//                         <Typography variant="caption" sx={{ 
//                           color: "#666",
//                           fontSize: "11px",
//                           fontWeight: 500
//                         }}>
//                           {answer.voteCount || 0} votes • Saved {formatDate(answer.createdAt)}
//                         </Typography>
//                       </Box>
//                     </Box>
                    
//                     <IconButton
//                       onClick={() => handleUnsaveAnswer(answer.answerId)}
//                       sx={{ 
//                         color: "#999",
//                         bgcolor: "rgba(255, 255, 255, 0.8)",
//                         backdropFilter: "blur(10px)",
//                         "&:hover": { 
//                           color: "#ff4757",
//                           bgcolor: "rgba(255, 71, 87, 0.1)",
//                           transform: "scale(1.05)"
//                         },
//                         transition: "all 0.2s ease",
//                         boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
//                       }}
//                       size="small"
//                     >
//                       <Delete fontSize="small" />
//                     </IconButton>
//                   </Stack>
//                 </Box>
//               </Box>
//             ))}
//           </Box>
//         )}
//       </Box>
//     );
//   };

//   const LoadingSkeleton = () => (
//     <Box>
//       {[1, 2, 3].map((item) => (
//         <Box key={item} sx={{ 
//           mb: 2,
//           border: "1px solid #d6d9dc",
//           borderRadius: "3px",
//           bgcolor: "#ffffff"
//         }}>
//           <Box sx={{ p: "16px" }}>
//             <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
//               <Skeleton variant="text" width={60} height={16} />
//               <Skeleton variant="text" width={80} height={16} />
//               <Skeleton variant="text" width={70} height={16} />
//             </Stack>
            
//             <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1.5 }} />
//             <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            
//             <Stack direction="row" justifyContent="space-between" alignItems="center">
//               <Stack direction="row" spacing={1}>
//                 <Skeleton variant="rectangular" width={50} height={22} />
//                 <Skeleton variant="rectangular" width={70} height={22} />
//               </Stack>
//               <Skeleton variant="text" width={80} height={16} />
//             </Stack>
//           </Box>
//         </Box>
//       ))}
//     </Box>
//   );

//   if (!user?.uid) {
//     return (
//       <Box sx={{ p: 3, textAlign: "center" }}>
//         <Typography variant="h6" sx={{ mb: 2 }}>
//           Please log in to view your saved content
//         </Typography>
//         <Button
//           onClick={() => navigate("/login")}
//           variant="contained"
//           sx={{ bgcolor: "#0a95ff", "&:hover": { bgcolor: "#0074cc" } }}
//         >
//           Log In
//         </Button>
//       </Box>
//     );
//   }

//   const totalItems = groupedSavedItems.reduce((sum, group) => {
//     return sum + (group.isQuestionSaved ? 1 : 0) + group.savedAnswers.length + (group.savedAiAnswer ? 1 : 0);
//   }, 0);

//   return (
//     <Box sx={{ 
//       maxWidth: "1200px", 
//       mx: "auto", 
//       p: "32px",
//       bgcolor: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
//       minHeight: "100vh"
//     }}>
//       {/* Header */}
//       <Box sx={{
//         mb: 4,
//         p: "32px",
//         borderRadius: "16px",
//         background: "linear-gradient(135deg, #c6cef0ff 0%, #a363e4ff 100%)",
//         boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
//         color: "white"
//       }}>
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Box>
//             <Typography variant="h3" sx={{ 
//               fontWeight: 700, 
//               fontSize: "32px",
//               mb: 1,
//               background: "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)",
//               backgroundClip: "text",
//               WebkitBackgroundClip: "text",
//               WebkitTextFillColor: "transparent"
//             }}>
//               All saves
//             </Typography>
//             <Typography variant="body1" sx={{ 
//               color: "rgba(255, 255, 255, 0.8)",
//               fontSize: "16px",
//               fontWeight: 500
//             }}>
//               {totalItems} saved item{totalItems !== 1 ? 's' : ''} • Your curated knowledge collection
//             </Typography>
//           </Box>
          
//           <Button
//             onClick={() => navigate("/add-question")}
//             variant="contained"
//             sx={{
//               bgcolor: "rgba(255, 255, 255, 0.2)",
//               color: "white",
//               textTransform: "none",
//               fontSize: "14px",
//               px: "20px",
//               py: "12px",
//               borderRadius: "12px",
//               fontWeight: 600,
//               backdropFilter: "blur(10px)",
//               boxShadow: "0 4px 15px rgba(255, 255, 255, 0.2)",
//               border: "1px solid rgba(255, 255, 255, 0.3)",
//               "&:hover": { 
//                 bgcolor: "rgba(255, 255, 255, 0.3)",
//                 transform: "translateY(-2px)",
//                 boxShadow: "0 8px 25px rgba(255, 255, 255, 0.3)"
//               },
//               transition: "all 0.3s ease"
//             }}
//           >
//             Ask Question
//           </Button>
//         </Stack>
//       </Box>

//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
//           {error}
//         </Alert>
//       )}

//       {loading ? (
//         <LoadingSkeleton />
//       ) : (
//         <Box>
//           {groupedSavedItems.length === 0 ? (
//             <Box sx={{ 
//               textAlign: "center", 
//               py: 8,
//               border: "1px solid #d6d9dc",
//               borderRadius: "3px",
//               bgcolor: "#fafafb"
//             }}>
//               <BookmarkBorder sx={{ fontSize: 48, color: "#9fa6ad", mb: 2 }} />
//               <Typography variant="h6" sx={{ 
//                 color: "#6a737c", 
//                 mb: 1,
//                 fontSize: "21px",
//                 fontWeight: 400
//               }}>
//                 No saved items yet
//               </Typography>
//               <Typography variant="body2" sx={{ 
//                 color: "#6a737c",
//                 fontSize: "13px"
//               }}>
//                 Save questions, answers, and AI responses to find them easily later
//               </Typography>
//             </Box>
//           ) : (
//             groupedSavedItems.map((group, index) => (
//               <QuestionGroupCard key={`${group.questionId}-${index}`} group={group} />
//             ))
//           )}
//         </Box>
//       )}
//     </Box>
//   );
// };

// export default UserSavedDetails;

// commented on 27 september 2025
// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Chip,
//   IconButton,
//   Card,
//   CardContent,
//   Stack,
//   Skeleton,
//   Alert,
//   Button,
// } from "@mui/material";
// import {
//   BookmarkBorder,
//   Delete,
//   SmartToy,
//   CheckCircle,
// } from "@mui/icons-material";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import moment from "moment";
// import ReactHtmlParser from "react-html-parser";

// const UserSavedDetails = () => {
//   const [groupedSavedItems, setGroupedSavedItems] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   const user = JSON.parse(localStorage.getItem("UserDetails")) || {};
//   const navigate = useNavigate();

//   useEffect(() => {
//     if (user?.uid) {
//       fetchAndGroupSavedContent();
//     }
//   }, [user?.uid]);

//   const fetchAndGroupSavedContent = async () => {
//     setLoading(true);
//     setError("");
//     try {
//       console.log("Fetching saved content for user:", user.uid);
      
//       const [questionsResponse, answersResponse, aiAnswersResponse] = await Promise.all([
//         axios.get(`${process.env.REACT_APP_FORUM_API}/api/save?user=${user.uid}`),
//         axios.get(`${process.env.REACT_APP_FORUM_API}/api/save-answer?user=${user.uid}`),
//         axios.get(`${process.env.REACT_APP_FORUM_API}/api/save-ai-answer?user=${user.uid}`),
//       ]);

//       const savedQuestions = questionsResponse.data.data || [];
//       const savedAnswers = answersResponse.data.data || [];
//       const savedAiAnswers = aiAnswersResponse.data.data || [];

//       console.log("Raw data:", { savedQuestions, savedAnswers, savedAiAnswers });

//       // Debug: Check structure of first AI answer
//       if (savedAiAnswers.length > 0) {
//         console.log("First AI answer structure:", savedAiAnswers[0]);
//         console.log("First AI answer questionId:", savedAiAnswers[0].questionId);
//         console.log("Type of questionId:", typeof savedAiAnswers[0].questionId);
//       }

//       // Create a map to group by questionId
//       const questionGroups = new Map();

//       // Process saved questions first
//       savedQuestions.forEach(question => {
//         // Extract the actual questionId
//         const questionId = question.questionId?._id || question.questionId;
//         console.log("Processing saved question, extracted questionId:", questionId);
        
//         questionGroups.set(questionId, {
//           questionId: questionId,
//           questionData: question,
//           isQuestionSaved: true,
//           savedAnswers: [],
//           savedAiAnswer: null,
//           savedAt: question.createdAt
//         });
//       });

//       // Process saved answers
//       savedAnswers.forEach(answer => {
//         // Extract the actual questionId - check if questionId is an object
//         const questionId = answer.questionId?._id || answer.questionId;
//         console.log("Processing saved answer, extracted questionId:", questionId);
        
//         if (questionGroups.has(questionId)) {
//           // Add to existing question group
//           questionGroups.get(questionId).savedAnswers.push(answer);
//         } else {
//           // Create new group for standalone answer - use the full questionId object if available
//           const questionDataForAnswer = answer.questionId && typeof answer.questionId === 'object' 
//             ? answer.questionId 
//             : { questionId: questionId, title: answer.questionTitle };
            
//           questionGroups.set(questionId, {
//             questionId: questionId,
//             questionData: questionDataForAnswer,
//             isQuestionSaved: false,
//             savedAnswers: [answer],
//             savedAiAnswer: null,
//             savedAt: answer.createdAt
//           });
//         }
//       });

//       // Process saved AI answers
//       savedAiAnswers.forEach((aiAnswer, index) => {
//         console.log(`Processing AI answer ${index}:`, aiAnswer);
//         console.log(`AI answer questionId type:`, typeof aiAnswer.questionId);
//         console.log(`AI answer questionId value:`, aiAnswer.questionId);
        
//         // Extract questionId and use the full questionId object if available
//         const questionId = aiAnswer.questionId?._id || aiAnswer.questionId;
//         const questionDataForAi = aiAnswer.questionId && typeof aiAnswer.questionId === 'object' 
//           ? aiAnswer.questionId 
//           : { questionId: questionId, title: aiAnswer.questionTitle || "Question with AI Answer" };
          
//         console.log(`Extracted questionId for AI answer:`, questionId);
//         console.log(`Question data for AI:`, questionDataForAi);
        
//         if (questionGroups.has(questionId)) {
//           // Add to existing question group
//           questionGroups.get(questionId).savedAiAnswer = aiAnswer;
//         } else {
//           // Create new group for standalone AI answer
//           questionGroups.set(questionId, {
//             questionId: questionId,
//             questionData: questionDataForAi,
//             isQuestionSaved: false,
//             savedAnswers: [],
//             savedAiAnswer: aiAnswer,
//             savedAt: aiAnswer.createdAt
//           });
//         }
//       });

//       // Convert to array and sort by save date
//       const groupedArray = Array.from(questionGroups.values())
//         .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));

//       console.log("Grouped data:", groupedArray);
//       setGroupedSavedItems(groupedArray);
//     } catch (err) {
//       console.error("Error fetching saved content:", err);
//       setError("Failed to load saved content. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatDate = (dateString) => {
//     const createdAt = moment(dateString);
//     const now = moment();
//     return now.diff(createdAt, "hours") < 24
//       ? createdAt.fromNow()
//       : createdAt.format("MMM DD, YYYY");
//   };

//   const truncateContent = (content, maxLength = 200) => {
//     if (!content) return "";
//     const textContent = content.replace(/<[^>]*>/g, "");
//     return textContent.length > maxLength
//       ? textContent.substring(0, maxLength) + "..."
//       : textContent;
//   };

//   const handleQuestionClick = (item) => {
//     console.log("=== Question Click Debug ===");
//     console.log("Raw item:", item);
//     console.log("Item questionId:", item.questionId);
//     console.log("Type of questionId:", typeof item.questionId);
    
//     // Extract the actual questionId - handle various formats
//     let questionId;
    
//     if (typeof item.questionId === 'string') {
//       questionId = item.questionId;
//     } else if (typeof item.questionId === 'object' && item.questionId !== null) {
//       // Check various possible properties
//       questionId = item.questionId._id || 
//                  item.questionId.toString() ||
//                  String(item.questionId);
//     } else {
//       questionId = String(item.questionId);
//     }
    
//     console.log("Extracted questionId:", questionId);
//     console.log("Final questionId type:", typeof questionId);
    
//     if (!questionId || questionId === '[object Object]' || questionId === 'undefined') {
//       console.error('Invalid questionId extracted:', questionId);
//       setError('Invalid question ID');
//       return;
//     }
    
//     // Use the correct route (singular 'question' not 'questions')
//     let url = `/question?q=${questionId}`;
//     console.log("Navigation URL:", url);
    
//     navigate(url);
//   };

//   const handleUnsaveQuestion = async (questionIdParam) => {
//     try {
//       // Extract the actual questionId
//       const questionId = typeof questionIdParam === 'object' ? 
//         (questionIdParam._id || questionIdParam.questionId || String(questionIdParam)) : 
//         String(questionIdParam);
        
//       console.log("Unsaving question with ID:", questionId);
      
//       await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save/${questionId}`, {
//         data: { user } // Pass user in request body
//       });
//       await fetchAndGroupSavedContent(); // Refresh data
//     } catch (err) {
//       console.error("Error unsaving question:", err);
//       setError("Failed to unsave question. Please try again.");
//     }
//   };

//   const handleUnsaveAnswer = async (answerIdParam) => {
//     try {
//       // Extract the actual answerId
//       const answerId = typeof answerIdParam === 'object' ? 
//         (answerIdParam._id || answerIdParam.answerId || String(answerIdParam)) : 
//         String(answerIdParam);
        
//       console.log("Unsaving answer with ID:", answerId);
      
//       await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save-answer/${answerId}`, {
//         data: { user }
//       });
//       await fetchAndGroupSavedContent(); // Refresh data
//     } catch (err) {
//       console.error("Error unsaving answer:", err);
//       setError("Failed to unsave answer. Please try again.");
//     }
//   };

//   const handleUnsaveAiAnswer = async (questionIdParam) => {
//     try {
//       console.log("=== AI Answer Delete Debug ===");
//       console.log("Raw questionIdParam:", questionIdParam);
//       console.log("Type:", typeof questionIdParam);
//       console.log("JSON stringified:", JSON.stringify(questionIdParam));
      
//       // Extract the actual questionId - handle various formats
//       let questionId;
      
//       if (typeof questionIdParam === 'string') {
//         questionId = questionIdParam;
//       } else if (typeof questionIdParam === 'object' && questionIdParam !== null) {
//         // Check various possible properties
//         questionId = questionIdParam._id || 
//                    questionIdParam.questionId || 
//                    questionIdParam.id ||
//                    String(questionIdParam);
//       } else {
//         questionId = String(questionIdParam);
//       }
      
//       console.log("Extracted questionId:", questionId);
//       console.log("Final questionId type:", typeof questionId);
      
//       if (!questionId || questionId === '[object Object]' || questionId === 'undefined') {
//         throw new Error('Invalid questionId extracted: ' + questionId);
//       }
      
//       await axios.delete(`${process.env.REACT_APP_FORUM_API}/api/save-ai-answer/${questionId}`, {
//         data: { user }
//       });
//       await fetchAndGroupSavedContent(); // Refresh data
//     } catch (err) {
//       console.error("Error unsaving AI answer:", err);
//       setError("Failed to unsave AI answer. Please try again.");
//     }
//   };

//   const QuestionGroupCard = ({ group }) => {
//     const { questionData, isQuestionSaved, savedAnswers, savedAiAnswer } = group;
    
//     // Calculate answer count - check both saved question and nested question data
//     const answerCount = questionData.answerDetails?.length || 
//                        questionData.answerCount || 
//                        (savedAnswers.length + (savedAiAnswer ? 1 : 0)) || 
//                        0;
    
//     return (
//       <Card sx={{ 
//         mb: { xs: 2, md: 3 }, 
//         border: "1px solid #e3e6e8", 
//         borderRadius: 1,
//         minHeight: { xs: "120px", md: "140px" }
//       }}>
//         <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
//           {/* Question Stats - Show for all items, with proper answer count */}
//           <Stack 
//             direction={{ xs: "column", sm: "row" }} 
//             spacing={{ xs: 1, sm: 3 }} 
//             sx={{ 
//               mb: { xs: 1.5, md: 2 }, 
//               fontSize: { xs: "0.75rem", md: "0.875rem" }, 
//               color: "#6a737c"
//             }}
//           >
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography sx={{ 
//                 fontWeight: 600, 
//                 color: "#0c0d0e",
//                 fontSize: { xs: "0.875rem", md: "1rem" }
//               }}>
//                 {questionData.voteCount || 0}
//               </Typography>
//               <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
//                 votes
//               </Typography>
//             </Stack>
            
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography sx={{ 
//                 fontWeight: 600, 
//                 color: answerCount > 0 ? "#2f6f44" : "#6a737c",
//                 fontSize: { xs: "0.875rem", md: "1rem" }
//               }}>
//                 {answerCount}
//               </Typography>
//               <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
//                 {answerCount === 1 ? "answer" : "answers"}
//               </Typography>
//             </Stack>
            
//             {questionData.viewCount && (
//               <Stack direction="row" spacing={0.5} alignItems="center">
//                 <Typography sx={{ 
//                   fontWeight: 600, 
//                   color: "#0c0d0e",
//                   fontSize: { xs: "0.875rem", md: "1rem" }
//                 }}>
//                   {questionData.viewCount}
//                 </Typography>
//                 <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
//                   views
//                 </Typography>
//               </Stack>
//             )}
//           </Stack>

//           {/* Question Title and Content */}
//           <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
//             <Box sx={{ flex: 1 }}>
//               <Typography
//                 variant="h6"
//                 onClick={() => handleQuestionClick({ questionId: questionData.questionId || questionData._id })}
//                 sx={{
//                   color: "#0a95ff",
//                   cursor: "pointer",
//                   fontSize: { xs: "1rem", md: "1.125rem" },
//                   fontWeight: 400,
//                   lineHeight: 1.3,
//                   mb: 1,
//                   "&:hover": { color: "#0074cc" },
//                 }}
//               >
//                 {questionData.title}
//               </Typography>

//               {/* Question Body (only if question is saved) */}
//               {isQuestionSaved && questionData.body && (
//                 <Typography
//                   variant="body2"
//                   sx={{ 
//                     color: "#0c0d0e", 
//                     lineHeight: 1.6, 
//                     mb: 1,
//                     fontSize: { xs: "0.875rem", md: "1rem" }
//                   }}
//                 >
//                   {ReactHtmlParser(truncateContent(questionData.body, 150))}
//                 </Typography>
//               )}

//               {/* Tags (only if question is saved) */}
//               {isQuestionSaved && questionData.tagDetails && questionData.tagDetails.length > 0 && (
//                 <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
//                   {questionData.tagDetails.map((tag, index) => (
//                     <Chip
//                       key={index}
//                       label={tag.name}
//                       size="small"
//                       sx={{
//                         bgcolor: "#e1ecf4",
//                         color: "#39739d",
//                         fontSize: { xs: "0.7rem", md: "0.75rem" },
//                         height: { xs: "20px", md: "22px" },
//                         "&:hover": { bgcolor: "#d0e3f1" },
//                       }}
//                     />
//                   ))}
//                 </Stack>
//               )}
//             </Box>

//             {/* Unsave Question Button (only if question is saved) */}
//             {isQuestionSaved && (
//               <IconButton
//                 onClick={() => handleUnsaveQuestion(group.questionId)}
//                 sx={{ 
//                   color: "#6a737c",
//                   "&:hover": { color: "#d0392d" },
//                   ml: 1
//                 }}
//                 size="small"
//               >
//                 <Delete fontSize="small" />
//               </IconButton>
//             )}
//           </Stack>

//           {/* Saved AI Answer (indented under question) */}
//           {savedAiAnswer && (
//             <Box sx={{ ml: { xs: 1, md: 2 }, mb: 2 }}>
//               <Box sx={{ 
//                 bgcolor: "#f0f8ff", 
//                 border: "2px solid #0a95ff", 
//                 borderRadius: 1, 
//                 p: { xs: 1.5, md: 2 },
//                 position: "relative"
//               }}>
//                 <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                   <Box sx={{ flex: 1 }}>
//                     <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                       <SmartToy sx={{ color: "#0a95ff", fontSize: "1rem" }} />
//                       <Typography variant="caption" sx={{ 
//                         color: "#0a95ff", 
//                         fontWeight: 600,
//                         fontSize: { xs: "0.75rem", md: "0.8rem" }
//                       }}>
//                         SAVED AI ANSWER
//                       </Typography>
//                     </Stack>
//                     <Typography variant="body2" sx={{ 
//                       color: "#0c0d0e", 
//                       lineHeight: 1.6, 
//                       mb: 1,
//                       fontSize: { xs: "0.875rem", md: "1rem" }
//                     }}>
//                       {truncateContent(savedAiAnswer.aiAnswer, 200)}
//                     </Typography>
//                     <Typography variant="caption" sx={{ 
//                       color: "#6a737c",
//                       fontSize: { xs: "0.7rem", md: "0.75rem" }
//                     }}>
//                       {savedAiAnswer.voteCount || 0} votes • Saved {formatDate(savedAiAnswer.createdAt)}
//                     </Typography>
//                   </Box>
//                   <IconButton
//                     onClick={() => {
//                       handleUnsaveAiAnswer(group.questionId);
//                     }}
//                     sx={{ 
//                       color: "#6a737c",
//                       "&:hover": { color: "#d0392d" },
//                       ml: 1
//                     }}
//                     size="small"
//                   >
//                     <Delete fontSize="small" />
//                   </IconButton>
//                 </Stack>
//               </Box>
//             </Box>
//           )}

//           {/* Saved Answers (indented under question) */}
//           {savedAnswers.map((answer, index) => (
//             <Box key={index} sx={{ ml: { xs: 1, md: 2 }, mb: 2 }}>
//               <Box sx={{ 
//                 bgcolor: "#f8f9fa", 
//                 border: "2px solid #2f6f44", 
//                 borderRadius: 1, 
//                 p: { xs: 1.5, md: 2 },
//                 position: "relative"
//               }}>
//                 <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                   <Box sx={{ flex: 1 }}>
//                     <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                       <CheckCircle sx={{ color: "#2f6f44", fontSize: "1rem" }} />
//                       <Typography variant="caption" sx={{ 
//                         color: "#2f6f44", 
//                         fontWeight: 600,
//                         fontSize: { xs: "0.75rem", md: "0.8rem" }
//                       }}>
//                         SAVED ANSWER
//                       </Typography>
//                     </Stack>
//                     <Typography variant="body2" sx={{ 
//                       color: "#0c0d0e", 
//                       lineHeight: 1.6, 
//                       mb: 1,
//                       fontSize: { xs: "0.875rem", md: "1rem" }
//                     }}>
//                       {ReactHtmlParser(truncateContent(answer.answer, 200))}
//                     </Typography>
//                     <Typography variant="caption" sx={{ 
//                       color: "#6a737c",
//                       fontSize: { xs: "0.7rem", md: "0.75rem" }
//                     }}>
//                       {answer.voteCount || 0} votes • Saved {formatDate(answer.createdAt)}
//                     </Typography>
//                   </Box>
//                   <IconButton
//                     onClick={() => handleUnsaveAnswer(answer.answerId)}
//                     sx={{ 
//                       color: "#6a737c",
//                       "&:hover": { color: "#d0392d" },
//                       ml: 1
//                     }}
//                     size="small"
//                   >
//                     <Delete fontSize="small" />
//                   </IconButton>
//                 </Stack>
//               </Box>
//             </Box>
//           ))}

//           {/* Footer with save info (only if question is saved) */}
//           {isQuestionSaved && (
//             <Typography variant="caption" sx={{ 
//               color: "#6a737c",
//               fontSize: { xs: "0.7rem", md: "0.75rem" }
//             }}>
//               Saved in For later {formatDate(group.savedAt)}
//             </Typography>
//           )}
//         </CardContent>
//       </Card>
//     );
//   };

//   const LoadingSkeleton = () => (
//     <Box>
//       {[1, 2, 3].map((item) => (
//         <Card key={item} sx={{ 
//           mb: { xs: 2, md: 3 }, 
//           border: "1px solid #e3e6e8",
//           minHeight: { xs: "120px", md: "140px" }
//         }}>
//           <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
//             {/* Stats Row */}
//             <Stack 
//               direction={{ xs: "column", sm: "row" }} 
//               spacing={{ xs: 1, sm: 3 }} 
//               sx={{ mb: { xs: 1.5, md: 2 } }}
//             >
//               <Skeleton variant="text" width={80} height={24} />
//               <Skeleton variant="text" width={100} height={24} />
//               <Skeleton variant="text" width={90} height={24} />
//             </Stack>
            
//             {/* Title */}
//             <Skeleton 
//               variant="text" 
//               width="85%" 
//               height={32} 
//               sx={{ mb: { xs: 1, md: 1.5 } }} 
//             />
            
//             {/* Content Area */}
//             <Skeleton 
//               variant="rectangular" 
//               height={{ xs: 60, md: 80 }} 
//               sx={{ mb: { xs: 1.5, md: 2 } }} 
//             />
            
//             {/* Tags */}
//             <Stack direction="row" spacing={1} sx={{ mb: { xs: 1.5, md: 2 } }}>
//               <Skeleton variant="rectangular" width={60} height={24} />
//               <Skeleton variant="rectangular" width={80} height={24} />
//             </Stack>
            
//             {/* Footer */}
//             <Skeleton 
//               variant="text" 
//               width="30%" 
//               height={20} 
//             />
//           </CardContent>
//         </Card>
//       ))}
//     </Box>
//   );
//   const LoadingSkeleton2 = () => (
//   <Box sx={{ width: "100%" }}>
//     {[1, 2, 3].map((item) => (
//       <Card
//         key={item}
//         sx={{
//           width: "100%", // make card full width
//           mb: { xs: 2, md: 1 },
//           border: "1px solid #e3e6e8",
//           minHeight: { xs: "120px", md: "140px" },
//         }}
//       >
//         <CardContent sx={{ p: { xs: 2, md: 2.5 }, width: "100%" }}>
//           {/* Stats Row */}
//           <Stack
//             direction={{ xs: "column", sm: "row" }}
//             spacing={{ xs: 1, sm: 3 }}
//             sx={{ mb: { xs: 1.5, md: 2 }, width: "100%" }}
//           >
//             <Skeleton variant="text" width={80} height={24} />
//             <Skeleton variant="text" width={100} height={24} />
//             <Skeleton variant="text" width={90} height={24} />
//           </Stack>

//           {/* Title */}
//           <Skeleton
//             variant="text"
//             width="100%" // stretch full width
//             height={32}
//             sx={{ mb: { xs: 1, md: 1.5 } }}
//           />

//           {/* Content Area */}
//           <Skeleton
//             variant="rectangular"
//             width="100%" // stretch full width
//             height={{ xs: 60, md: 80 }}
//             sx={{ mb: { xs: 1.5, md: 2 } }}
//           />

//           {/* Tags */}
//           <Stack
//             direction="row"
//             spacing={1}
//             sx={{ mb: { xs: 1.5, md: 2 }, width: "100%" }}
//           >
//             <Skeleton variant="rectangular" width={60} height={24} />
//             <Skeleton variant="rectangular" width={80} height={24} />
//           </Stack>

//           {/* Footer */}
//           <Skeleton variant="text" width="40%" height={20} />
//         </CardContent>
//       </Card>
//     ))}
//   </Box>
// );


//   if (!user?.uid) {
//     return (
//       <Box sx={{ p: 3, textAlign: "center" }}>
//         <Typography variant="h6" sx={{ mb: 2 }}>
//           Please log in to view your saved content
//         </Typography>
//         <Button
//           onClick={() => navigate("/login")}
//           variant="contained"
//           sx={{ bgcolor: "#0a95ff", "&:hover": { bgcolor: "#0074cc" } }}
//         >
//           Log In
//         </Button>
//       </Box>
//     );
//   }

//   const totalItems = groupedSavedItems.reduce((sum, group) => {
//     return sum + (group.isQuestionSaved ? 1 : 0) + group.savedAnswers.length + (group.savedAiAnswer ? 1 : 0);
//   }, 0);

//   return (
//     <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: "900px", mx: "auto" }}>
//       {/* Header */}
//       <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
//         <Typography variant="h4" sx={{ fontWeight: 600, color: "#0c0d0e" }}>
//           All saves
//         </Typography>
//         <Button
//           onClick={() => navigate("/add-question")}
//           variant="contained"
//           sx={{
//             bgcolor: "#0a95ff",
//             color: "white",
//             textTransform: "none",
//             "&:hover": { bgcolor: "#0074cc" }
//           }}
//         >
//           Ask Question
//         </Button>
//       </Stack>

//       <Typography variant="body1" sx={{ mb: 3, color: "#0c0d0e" }}>
//         {totalItems} saved item{totalItems !== 1 ? 's' : ''}
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
//           {error}
//         </Alert>
//       )}

//       {loading ? (
//         <LoadingSkeleton />
//       ) : (
//         <Box>
//           {groupedSavedItems.length === 0 ? (
//             <Box sx={{ textAlign: "center", py: 6 }}>
//               <BookmarkBorder sx={{ fontSize: 64, color: "#6a737c", mb: 2 }} />
//               <Typography variant="h6" sx={{ color: "#6a737c", mb: 1 }}>
//                 No saved items yet
//               </Typography>
//               <Typography variant="body2" sx={{ color: "#6a737c" }}>
//                 Save questions, answers, and AI responses to find them easily later
//               </Typography>
//             </Box>
//           ) : (
//             groupedSavedItems.map((group, index) => (
//               <QuestionGroupCard key={`${group.questionId}-${index}`} group={group} />
//             ))
//           )}
//         </Box>
//       )}
//     </Box>
//   );
// };

// export default UserSavedDetails;